import React, { useMemo } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import i18n from 'i18n-react';
import { Link } from 'react-router-dom';

// import { milestoneDateDisplay, milestoneHasValue } from '../../helper/formatDateOnly';
import glassWidgetHelpers from 'helpers/glassWidgetsHelpers';

// import { GlassCard } from '../../GlassCard/GlassCard';
import GlassCard from '../../GlassCard';

/**
 * @param firstName
 * @param lastName
 */
const getInitialsFromName = (firstName, lastName) => {
  const first = (firstName ?? '').trim().charAt(0);
  const last = (lastName ?? '').trim().charAt(0);
  if (first || last) {
    return `${first.toUpperCase()}${last.toUpperCase()}`;
  }
  return null;
};

/**
 * @param root0
 * @param root0.span
 * @param root0.variant
 * @param root0.addContentGutter
 * @param root0.metallicTiers
 * @param root0.advisorMilestones
 * @param root0.profilePictureUrl
 * @param root0.firstName
 * @param root0.lastName
 * @param root0.isLoggedIn
 * @param root0.profileFrameUrl
 * @param root0.lightTheme
 */
const ProgramStatusCard = ({
  span,
  variant,
  addContentGutter,
  metallicTiers = false,
  lightTheme = false,
  advisorMilestones = null,
  profilePictureUrl = null,
  firstName,
  lastName,
  isLoggedIn,
  profileFrameUrl,
}) => {
  const initials = useMemo(
    () => getInitialsFromName(firstName, lastName),
    [firstName, lastName],
  );

  const hasRegisteredOn =
    advisorMilestones !== null &&
    glassWidgetHelpers.milestoneHasValue(
      advisorMilestones.joined_on_display,
      advisorMilestones.joined_on,
    );
  const hasGraduatedOn =
    advisorMilestones !== null &&
    glassWidgetHelpers.milestoneHasValue(
      advisorMilestones.graduated_on_display,
      advisorMilestones.graduated_on,
    );
  const hasBadgesEarned =
    advisorMilestones !== null && advisorMilestones.total_badges_earned > 0;

  const joinedStatDisplay = glassWidgetHelpers.milestoneDateDisplay(
    advisorMilestones?.joined_on_display,
    advisorMilestones?.joined_on,
  );
  const graduatedStatDisplay = glassWidgetHelpers.milestoneDateDisplay(
    advisorMilestones?.graduated_on_display,
    advisorMilestones?.graduated_on,
  );

  const advisorTierStatus = advisorMilestones?.tier_status;
  const tierSlugRaw = (advisorTierStatus?.tier ?? '').trim();
  const tierSlugNorm =
    tierSlugRaw !== '' && tierSlugRaw.toLowerCase() !== 'none'
      ? tierSlugRaw.toLowerCase()
      : '';
  const showTierRow =
    metallicTiers &&
    advisorMilestones !== null &&
    advisorTierStatus !== undefined &&
    tierSlugNorm !== '';
  const tierValueDisplay =
    tierSlugRaw !== '' && tierSlugRaw.toLowerCase() !== 'none'
      ? tierSlugRaw
      : (advisorTierStatus?.label.trim() ?? '');

  const badgeCountStat =
    advisorMilestones !== null ? advisorMilestones.total_badges_earned : 0;

  const hasAnyStatsContent =
    hasRegisteredOn ||
    hasGraduatedOn ||
    hasBadgesEarned ||
    (showTierRow && tierValueDisplay !== '');

  const headerAvatar = useMemo(() => {
    if (profilePictureUrl) {
      return (
        <div className="program-status-card__header-avatar">
          <Link
            to="/main/profile-image"
            className="program-status-card__avatar program-status-card__avatar--initials program-status-card__avatar--in-header"
          >
            <img
              src={profilePictureUrl}
              alt={
                initials !== null && initials !== ''
                  ? `profile-image-${initials}`
                  : 'Profile'
              }
            />
          </Link>
        </div>
      );
    }
    if (initials) {
      return (
        <div className="program-status-card__header-avatar">
          <Link
            to="/main/profile-image"
            className="program-status-card__avatar program-status-card__avatar--initials program-status-card__avatar--in-header"
          >
            <span
              className="program-status-card__avatar-initials"
              aria-hidden="true"
            >
              {initials}
            </span>
          </Link>
        </div>
      );
    }
    return null;
  }, [profilePictureUrl, initials]);

  const hasProfileFrameUrl = profileFrameUrl.trim() !== '';

  const actionButtons = useMemo(
    () => (
      <div className="program-status-card__actions">
        {isLoggedIn && (
          <Link
            to="/main/contact"
            className="program-status-card__action-btn"
          >
            <FontAwesomeIcon icon={['fas', 'comment-dots']} />
            <span>{i18n.translate('glassWidgets.programStatus.yourRep')}</span>
          </Link>
        )}
        {!isLoggedIn && (
          <span
            className="program-status-card__action-btn program-status-card__action-btn--disabled"
            aria-disabled="true"
          >
            <FontAwesomeIcon icon={['fas', 'comment-dots']} />
            <span>{i18n.translate('glassWidgets.programStatus.yourRep')}</span>
          </span>
        )}

        {isLoggedIn && (
          <button
            type="button"
            className="program-status-card__action-btn settings"
            data-trigger="settings"
            data-fancybox-rel="settings"
            disabled={!hasProfileFrameUrl}
          >
            <FontAwesomeIcon icon={['fas', 'user']} />
            <span>
              {i18n.translate('glassWidgets.programStatus.myProfile')}
            </span>
          </button>
        )}
        {!isLoggedIn && (
          <span
            className="program-status-card__action-btn program-status-card__action-btn--disabled"
            aria-disabled="true"
          >
            <FontAwesomeIcon icon={['fas', 'user']} />
            <span>
              {i18n.translate('glassWidgets.programStatus.myProfile')}
            </span>
          </span>
        )}

        {isLoggedIn && (
          <Link
            to="/main/favorites"
            className="program-status-card__action-btn"
          >
            <FontAwesomeIcon icon={['fas', 'heart']} />
            <span>
              {i18n.translate('glassWidgets.programStatus.favorites')}
            </span>
          </Link>
        )}
        {!isLoggedIn && (
          <span
            className="program-status-card__action-btn program-status-card__action-btn--disabled"
            aria-disabled="true"
          >
            <FontAwesomeIcon icon={['fas', 'heart']} />
            <span>
              {i18n.translate('glassWidgets.programStatus.favorites')}
            </span>
          </span>
        )}

        {isLoggedIn && (
          <Link
            to="/logout"
            className="program-status-card__action-btn program-status-card__action-btn--logout"
          >
            <FontAwesomeIcon icon={['fas', 'sign-out-alt']} />
            <span>{i18n.translate('general.logOut')}</span>
          </Link>
        )}

        {!isLoggedIn && (
          <Link to="/login" className="program-status-card__action-btn">
            <FontAwesomeIcon icon={['fas', 'sign-in-alt']} />
            <span>{i18n.translate('general.logIn')}</span>
          </Link>
        )}
      </div>
    ),
    [isLoggedIn, hasProfileFrameUrl],
  );

  return (
    <GlassCard
      icon={['fas', 'star']}
      title={i18n.translate('glassWidgets.programStatus.title')}
      span={span}
      variant={variant}
      addContentGutter={addContentGutter}
      lightTheme={lightTheme}
      className={clsx('program-status-card', {
        'program-status-card--tier-default': !metallicTiers || !tierSlugNorm,
        'program-status-card--tier-bronze':
          metallicTiers && tierSlugNorm === 'bronze',
        'program-status-card--tier-silver':
          metallicTiers && tierSlugNorm === 'silver',
        'program-status-card--tier-gold':
          metallicTiers && tierSlugNorm === 'gold',
        'program-status-card--tier-platinum':
          metallicTiers && tierSlugNorm === 'platinum',
      })}
      headerExtra={headerAvatar ?? undefined}
      bottomActions={actionButtons}
    >
      {hasAnyStatsContent && (
        <ul className="program-status-card__stats">
          {hasRegisteredOn && (
            <li className="program-status-card__stat">
              <FontAwesomeIcon
                icon={['fas', 'star']}
                className="program-status-card__stat-icon"
              />
              <span className="program-status-card__stat-label">
                {i18n.translate('glassWidgets.programStatus.registeredOn')}
              </span>
              <span className="program-status-card__stat-value">
                {joinedStatDisplay}
              </span>
            </li>
          )}
          {hasGraduatedOn && (
            <li className="program-status-card__stat">
              <FontAwesomeIcon
                icon={['fas', 'file-certificate']}
                className="program-status-card__stat-icon"
              />
              <span className="program-status-card__stat-label">
                {i18n.translate('glassWidgets.programStatus.graduatedOn')}
              </span>
              <span className="program-status-card__stat-value">
                {graduatedStatDisplay}
              </span>
            </li>
          )}
          {hasBadgesEarned && (
            <li className="program-status-card__stat">
              <FontAwesomeIcon
                icon={['fas', 'trophy']}
                className="program-status-card__stat-icon"
              />
              <span className="program-status-card__stat-label">
                {i18n.translate('glassWidgets.programStatus.totalBadgesEarned')}
              </span>
              <span className="program-status-card__stat-value">
                {badgeCountStat}
              </span>
            </li>
          )}

          {showTierRow && tierValueDisplay !== '' && (
            <li className="program-status-card__stat">
              <FontAwesomeIcon
                icon={['fas', 'award']}
                className="program-status-card__stat-icon"
              />
              <span className="program-status-card__stat-label">
                {i18n.translate('glassWidgets.programStatus.currentTier')}
              </span>
              <span
                className={clsx(
                  'program-status-card__stat-value program-status-card__tier-value',
                  {
                    'program-status-card__tier-value--bronze':
                      tierSlugNorm === 'bronze',
                    'program-status-card__tier-value--silver':
                      tierSlugNorm === 'silver',
                    'program-status-card__tier-value--gold':
                      tierSlugNorm === 'gold',
                    'program-status-card__tier-value--platinum':
                      tierSlugNorm === 'platinum',
                  },
                )}
              >
                <FontAwesomeIcon icon={['fas', 'award']} />
                {tierValueDisplay}
              </span>
            </li>
          )}
        </ul>
      )}

      {profileFrameUrl && (
        <a
          data-fancybox="settings"
          data-type="iframe"
          className="settings"
          href={profileFrameUrl}
          style={{ display: 'none' }}
          tabIndex={-1}
          aria-hidden="true"
        />
      )}
    </GlassCard>
  );
};

export default ProgramStatusCard;
